import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /**
   * Testo extra usato per il match della ricerca (es. codice articolo,
   * categoria). Se omesso, si cerca solo nel label.
   */
  searchableText?: string;
  /** Disabilita la riga (es. opzioni non selezionabili). */
  disabled?: boolean;
}

interface SearchableSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
}

/**
 * Drop-in replacement per Select shadcn quando la lista opzioni e' lunga e
 * serve una ricerca testuale. Usa Popover + cmdk Command sotto. Filtra su
 * label + searchableText (case-insensitive, accent-insensitive via cmdk).
 *
 * API minimale per coerenza col Select esistente:
 *   <SearchableSelect value={x} onValueChange={setX} options={[{value, label}]} />
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Seleziona…',
  searchPlaceholder = 'Cerca…',
  emptyMessage = 'Nessun risultato',
  className,
  triggerClassName,
  disabled,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0 w-[--radix-popover-trigger-width] min-w-[280px]', className)}
        align="start"
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue arriva come `${value}__${searchableText|label}` per
            // permettere la ricerca anche su codice/altro testo libero
            const haystack = itemValue.toLowerCase();
            const needle = search.toLowerCase().trim();
            if (!needle) return 1;
            return haystack.includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const composedKey = `${opt.value}__${(opt.searchableText ?? opt.label).toLowerCase()}`;
                return (
                  <CommandItem
                    key={opt.value}
                    value={composedKey}
                    disabled={opt.disabled}
                    onSelect={() => {
                      onValueChange(opt.value);
                      setOpen(false);
                    }}
                    className="text-sm"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === opt.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
